import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { BarChart3, RefreshCw, Download } from 'lucide-react';
import PosCheckoutView from '../components/PosCheckoutView';

const money = (c) => '$' + (Number(c || 0) / 100).toFixed(2);
const dollars = (c) => (Number(c || 0) / 100).toFixed(2);
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

const TABS = [
  { key: 'daily-sales', label: 'Daily Sales' },
  { key: 'items', label: 'Items & Categories' },
  { key: 'voids', label: 'Voids & Refunds' },
  { key: 'chip-wallet', label: 'Chip-Wallet Spend' },
  { key: 'checkout', label: 'Shift Checkout' },
];

function downloadCsv(filename, headers, rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const csv = [headers.join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function PosReports() {
  const [tab, setTab] = useState('daily-sales');
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (tab === 'checkout') { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.get(`/pos/reports/${tab}?start=${start}&end=${end}`);
      setData((d) => ({ ...d, [tab]: res.data.rows || [] }));
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [tab, start, end]);
  useEffect(() => { load(); }, [load]);

  const rows = data[tab] || [];

  return (
    <div className="p-6 max-w-6xl mx-auto text-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2"><BarChart3 className="w-6 h-6" /> POS Reports</h1>
        <button onClick={load} className="flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-lg hover:bg-gray-600"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
      </div>

      {tab !== 'checkout' && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <label className="text-sm text-gray-400">From</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2" />
          <label className="text-sm text-gray-400">To</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2" />
        </div>
      )}

      <div className="flex flex-wrap gap-1 mb-4 border-b border-gray-700">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg ${tab === t.key ? 'bg-gray-800 text-white border-b-2 border-indigo-500' : 'text-gray-400 hover:text-gray-200'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-gray-500 text-center py-12">Loading…</div>}
      {!loading && tab === 'daily-sales' && <DailySales rows={rows} />}
      {!loading && tab === 'items' && <Items rows={rows} />}
      {!loading && tab === 'voids' && <Voids rows={rows} />}
      {!loading && tab === 'chip-wallet' && <ChipWallet rows={rows} />}
      {tab === 'checkout' && <Checkout />}
    </div>
  );
}

function ExportBtn({ onClick }) {
  return (
    <button onClick={onClick} className="flex items-center gap-2 px-3 py-1.5 bg-emerald-800 text-emerald-100 rounded-lg text-sm hover:bg-emerald-700">
      <Download className="w-4 h-4" /> Export CSV
    </button>
  );
}
function Empty() { return <div className="text-gray-500 text-center py-12">No data for this range.</div>; }
const Th = ({ children, right }) => <th className={`px-3 py-2 text-xs uppercase tracking-wide text-gray-400 ${right ? 'text-right' : 'text-left'}`}>{children}</th>;
const Td = ({ children, right }) => <td className={`px-3 py-2 ${right ? 'text-right tabular-nums' : ''}`}>{children}</td>;

function DailySales({ rows }) {
  const totals = useMemo(() => rows.reduce((a, r) => ({
    orders: a.orders + r.orders, grossCents: a.grossCents + r.grossCents, taxCents: a.taxCents + r.taxCents,
    totalCents: a.totalCents + r.totalCents, cashCents: a.cashCents + r.cashCents, chipWalletCents: a.chipWalletCents + r.chipWalletCents, changeCents: a.changeCents + r.changeCents,
  }), { orders: 0, grossCents: 0, taxCents: 0, totalCents: 0, cashCents: 0, chipWalletCents: 0, changeCents: 0 }), [rows]);
  if (!rows.length) return <Empty />;
  const exp = () => downloadCsv('pos-daily-sales.csv',
    ['Date', 'Orders', 'Gross', 'Tax', 'Total', 'Cash', 'Chip Wallet', 'Change'],
    rows.map((r) => [r.businessDate, r.orders, dollars(r.grossCents), dollars(r.taxCents), dollars(r.totalCents), dollars(r.cashCents), dollars(r.chipWalletCents), dollars(r.changeCents)]));
  return (
    <div>
      <div className="flex justify-end mb-2"><ExportBtn onClick={exp} /></div>
      <div className="bg-gray-800 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-700"><Th>Date</Th><Th right>Orders</Th><Th right>Gross</Th><Th right>Tax</Th><Th right>Total</Th><Th right>Cash</Th><Th right>Chip Wallet</Th><Th right>Change</Th></tr></thead>
          <tbody className="divide-y divide-gray-700/50">
            {rows.map((r) => (
              <tr key={r.businessDate}><Td>{r.businessDate}</Td><Td right>{r.orders}</Td><Td right>{money(r.grossCents)}</Td><Td right>{money(r.taxCents)}</Td><Td right>{money(r.totalCents)}</Td><Td right>{money(r.cashCents)}</Td><Td right>{money(r.chipWalletCents)}</Td><Td right>{money(r.changeCents)}</Td></tr>
            ))}
          </tbody>
          <tfoot><tr className="border-t border-gray-600 font-bold"><Td>Total</Td><Td right>{totals.orders}</Td><Td right>{money(totals.grossCents)}</Td><Td right>{money(totals.taxCents)}</Td><Td right>{money(totals.totalCents)}</Td><Td right>{money(totals.cashCents)}</Td><Td right>{money(totals.chipWalletCents)}</Td><Td right>{money(totals.changeCents)}</Td></tr></tfoot>
        </table>
      </div>
    </div>
  );
}

function Items({ rows }) {
  if (!rows.length) return <Empty />;
  const exp = () => downloadCsv('pos-item-performance.csv', ['Item', 'Category', 'Qty', 'Revenue'],
    rows.map((r) => [r.name, r.category, r.qty, dollars(r.revenueCents)]));
  return (
    <div>
      <div className="flex justify-end mb-2"><ExportBtn onClick={exp} /></div>
      <div className="bg-gray-800 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-700"><Th>Item</Th><Th>Category</Th><Th right>Qty</Th><Th right>Revenue</Th></tr></thead>
          <tbody className="divide-y divide-gray-700/50">
            {rows.map((r, i) => (<tr key={i}><Td>{r.name}</Td><Td>{r.category}</Td><Td right>{r.qty}</Td><Td right>{money(r.revenueCents)}</Td></tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Voids({ rows }) {
  if (!rows.length) return <Empty />;
  const exp = () => downloadCsv('pos-voids-refunds.csv', ['Order', 'Date', 'Customer', 'Total', 'Refunded', 'Tender', 'Voided By', 'Reason'],
    rows.map((r) => [r.orderNumber, r.businessDate, r.customerName || '', dollars(r.totalCents), dollars(r.refundedCents), r.tenders, r.voidedBy || '', r.reason || '']));
  return (
    <div>
      <div className="flex justify-end mb-2"><ExportBtn onClick={exp} /></div>
      <div className="bg-gray-800 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-700"><Th>Order</Th><Th>Date</Th><Th>Customer</Th><Th right>Total</Th><Th right>Refunded</Th><Th>Tender</Th><Th>Voided By</Th><Th>Reason</Th></tr></thead>
          <tbody className="divide-y divide-gray-700/50">
            {rows.map((r, i) => (<tr key={i}><Td>#{r.orderNumber}</Td><Td>{r.businessDate}</Td><Td>{r.customerName || '—'}</Td><Td right>{money(r.totalCents)}</Td><Td right>{money(r.refundedCents)}</Td><Td>{r.tenders}</Td><Td>{r.voidedBy || '—'}</Td><Td>{r.reason || '—'}</Td></tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChipWallet({ rows }) {
  const total = useMemo(() => rows.reduce((a, r) => a + r.amountCents, 0), [rows]);
  if (!rows.length) return <Empty />;
  const exp = () => downloadCsv('pos-chip-wallet-spend.csv', ['Player', 'Orders', 'Amount'],
    rows.map((r) => [r.name, r.orders, dollars(r.amountCents)]));
  return (
    <div>
      <div className="flex justify-end mb-2"><ExportBtn onClick={exp} /></div>
      <div className="bg-gray-800 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-700"><Th>Player</Th><Th right>Orders</Th><Th right>Amount</Th></tr></thead>
          <tbody className="divide-y divide-gray-700/50">
            {rows.map((r, i) => (<tr key={i}><Td>{r.name}</Td><Td right>{r.orders}</Td><Td right>{money(r.amountCents)}</Td></tr>))}
          </tbody>
          <tfoot><tr className="border-t border-gray-600 font-bold"><Td>Total</Td><Td right></Td><Td right>{money(total)}</Td></tr></tfoot>
        </table>
      </div>
    </div>
  );
}


const toCents = (d) => Math.round((parseFloat(d) || 0) * 100);
const todayStr = () => new Date().toISOString().slice(0, 10);

function printCheckout(c) {
  if (!c) return;
  const m = (x) => (x == null ? '\u2014' : '$' + (Number(x || 0) / 100).toFixed(2));
  const sgn = (x) => { if (x == null) return '\u2014'; const v = Number(x || 0); const s = '$' + (Math.abs(v) / 100).toFixed(2); return v < 0 ? '(' + s + ')' : s; };
  const line = (l, v) => `<tr><td>${l}</td><td style="text-align:right">${v}</td></tr>`;
  const comps = (c.compsByType || []).map((r) => line(r.label, m(r.cents))).join('');
  const cats = (c.salesByCategory || []).map((r) => line(r.category, m(r.cents))).join('');
  const html = `<!doctype html><html><head><title>Shift Checkout</title>
    <style>body{font-family:system-ui,Arial,sans-serif;padding:24px;max-width:520px;margin:auto}
    h1{font-size:18px;margin:0 0 2px}h2{font-size:12px;text-transform:uppercase;color:#666;margin:16px 0 4px}
    table{width:100%;border-collapse:collapse}td{padding:2px 0;font-size:13px}
    .big td{font-weight:700;font-size:15px}.status{margin-top:8px;text-align:center;font-weight:700;padding:6px;border:1px solid #000}</style></head>
    <body>
      <h1>Shift Checkout</h1>
      <div style="font-size:12px;color:#444">${c.staffName || 'All cashiers'} — ${c.businessDate}</div>
      <h2>Bottom line</h2><table>
        ${line('Cash due to house', m(c.cashToTurnInCents))}
        ${line('Cash turned in', m(c.countedCashCents))}
        <tr class="big">${'<td>Over / (Short)</td><td style="text-align:right">' + sgn(c.overShortCents) + '</td>'}</tr>
        ${line('Chip-wallet charged', m(c.chipWalletCents))}
      </table>
      <div class="status">${c.overShortCents == null ? 'NOT COUNTED' : (c.overShortCents === 0 ? 'BALANCED' : 'EXCEPTION — OVER/SHORT')}</div>
      <h2>Cash reconciliation</h2><table>
        ${line('Opening bank', m(c.openingBankCents))}
        ${line('Cash sales', m(c.cashSalesCents))}
        ${line('Cash refunds', m(c.cashRefundsCents))}
        ${line('Expected drawer', m(c.expectedCashCents))}
        ${line('Counted', m(c.countedCashCents))}
        ${line('Over / (Short)', sgn(c.overShortCents))}
      </table>
      <h2>Sales</h2><table>
        ${line('Orders', c.orderCount)}
        ${line('Gross', m(c.grossCents))}
        ${line('Tax', m(c.taxCents))}
        ${line('Comps / discounts', m(c.discountsCents))}
        <tr class="big">${'<td>Net</td><td style="text-align:right">' + m(c.netCents) + '</td>'}</tr>
      </table>
      ${comps ? '<h2>Comps &amp; discounts</h2><table>' + comps + '</table>' : ''}
      ${cats ? '<h2>Sales by category</h2><table>' + cats + '</table>' : ''}
      <div style="margin-top:32px;font-size:12px">Cashier: ______________________  Manager: ______________________</div>
    </body></html>`;
  const w = window.open('', '_blank', 'width=600,height=800');
  if (!w) return;
  w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
}

function Checkout() {
  const [date, setDate] = useState(todayStr());
  const [cashierKey, setCashierKey] = useState('');
  const [openingBank, setOpeningBank] = useState('');
  const [countedCash, setCountedCash] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);

  const compute = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ businessDate: date });
      if (cashierKey) params.set('cashierKey', cashierKey);
      if (openingBank !== '') params.set('openingBankCents', String(toCents(openingBank)));
      if (countedCash !== '') params.set('countedCashCents', String(toCents(countedCash)));
      const res = await api.get(`/pos/reports/checkout?${params.toString()}`);
      setResult(res.data.checkout);
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to load checkout'); }
    finally { setBusy(false); }
  }, [date, cashierId, openingBank, countedCash]);
  useEffect(() => { compute(); }, [date, cashierKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const cashiers = (result && result.cashiers) || [];

  const closeShift = async () => {
    if (!cashierKey) { toast.error('Pick a cashier to close their shift'); return; }
    if (countedCash === '') { toast.error('Enter the counted cash first'); return; }
    setClosing(true);
    try {
      const cashier = cashiers.find((c) => c.key === cashierKey);
      await api.post('/pos/shift-checkout', {
        businessDate: date, cashierKey, staffName: cashier ? cashier.staffName : null,
        openingBankCents: toCents(openingBank), countedCashCents: toCents(countedCash),
      });
      toast.success('Shift closed & recorded');
    } catch (e) { toast.error(e.response?.data?.error || 'Close failed'); }
    finally { setClosing(false); }
  };

  return (
    <div className="max-w-xl">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Business date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Cashier</label>
          <select value={cashierKey} onChange={(e) => setCashierKey(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 min-w-[160px]">
            <option value="">All cashiers</option>
            {cashiers.map((c) => <option key={c.key} value={c.key}>{c.staffName}{c.staffNumber ? ` (#${c.staffNumber})` : ''}</option>)}
          </select>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Opening bank ($)</label>
          <input value={openingBank} onChange={(e) => setOpeningBank(e.target.value)} inputMode="decimal" placeholder="0.00" className="w-28 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Cash counted ($)</label>
          <input value={countedCash} onChange={(e) => setCountedCash(e.target.value)} inputMode="decimal" placeholder="0.00" className="w-28 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2" />
        </div>
        <button onClick={compute} disabled={busy} className="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-500 disabled:opacity-40">{busy ? 'Updating…' : 'Update'}</button>
        {result && <button onClick={() => printCheckout(result)} className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600">Print</button>}
        <button onClick={closeShift} disabled={closing || !cashierId} className="px-4 py-2 bg-emerald-700 rounded-lg hover:bg-emerald-600 disabled:opacity-40">{closing ? 'Closing…' : 'Close shift'}</button>
      </div>
      {!result ? <div className="text-gray-500 text-center py-12">Loading…</div> : <PosCheckoutView checkout={result} />}
    </div>
  );
}
